import { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { CaretLeft, DotsThree } from '@phosphor-icons/react';
import NotificationBell from '../NotificationBell';
import { getNativePlatform, getNativeTitleOverride, hapticTap, isNativeTabRoot, nativeTitleForPath } from '../../lib/native';

const NativeTopBar = ({ user, setUser }) => {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const [menuOpen, setMenuOpen] = useState(false);
  const [override, setOverride] = useState(() => getNativeTitleOverride(pathname));
  const showBack = !isNativeTabRoot(pathname);
  const title = override || nativeTitleForPath(pathname);
  const isHome = pathname === '/' || pathname === '/classic' || pathname.startsWith('/themes');
  const isAndroid = getNativePlatform() === 'android';

  useEffect(() => {
    setOverride(getNativeTitleOverride(pathname));
    setMenuOpen(false);
    const onTitle = () => setOverride(getNativeTitleOverride(window.location.pathname));
    window.addEventListener('fz-native-title', onTitle);
    return () => window.removeEventListener('fz-native-title', onTitle);
  }, [pathname]);

  useEffect(() => {
    if (!menuOpen) return undefined;
    const onKey = (e) => { if (e.key === 'Escape') setMenuOpen(false); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [menuOpen]);

  const logout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    if (setUser) setUser(null);
    setMenuOpen(false);
    navigate('/login');
  };

  return (
    <header className={`native-top-bar${isAndroid ? ' is-android' : ' is-ios'}`} data-testid="native-top-bar">
      {showBack ? (
        <button
          type="button"
          aria-label="Back"
          data-testid="native-back-btn"
          className="native-icon-btn flex items-center justify-center text-white shrink-0"
          onClick={() => { hapticTap(); navigate(-1); }}
        >
          <CaretLeft size={24} weight="bold" />
        </button>
      ) : (
        !isAndroid && <div className={`native-icon-btn shrink-0${user ? ' native-icon-btn-double' : ''}`} aria-hidden />
      )}
      <h1 className={`flex-1 truncate text-white m-0 ${isAndroid ? 'text-left pl-3' : 'text-center'}`}>
        {isHome ? <span className="native-wordmark">FANZINGY</span> : title}
      </h1>
      {user && <NotificationBell user={user} className="native-icon-btn flex items-center justify-center shrink-0" />}
      <div className="relative native-icon-btn shrink-0">
        <button
          type="button"
          aria-label="More options"
          aria-haspopup="menu"
          aria-expanded={menuOpen}
          aria-controls="native-overflow-menu"
          data-testid="native-overflow-btn"
          className="native-icon-btn flex items-center justify-center text-white"
          onClick={() => setMenuOpen((v) => !v)}
        >
          <DotsThree size={26} weight="bold" />
        </button>
        {menuOpen && (
          <>
            <button
              type="button"
              className="fixed inset-0 z-40 bg-black/40"
              aria-label="Close menu"
              tabIndex={-1}
              onClick={() => setMenuOpen(false)}
            />
            <div
              id="native-overflow-menu"
              role="menu"
              aria-label="More options"
              className="absolute right-0 z-50 min-w-[200px] rounded-xl border border-white/10 bg-[#12081f]/95 backdrop-blur-xl shadow-2xl py-1 native-overflow-menu"
              data-testid="native-overflow-menu"
            >
              {user && (
                <button
                  type="button"
                  role="menuitem"
                  className="w-full text-left px-4 native-menu-item text-white/90"
                  onClick={() => { setMenuOpen(false); navigate('/notifications'); }}
                >
                  Notifications
                </button>
              )}
              {user && (
                <button
                  type="button"
                  role="menuitem"
                  className="w-full text-left px-4 native-menu-item text-white/90"
                  onClick={() => { setMenuOpen(false); navigate('/invite'); }}
                >
                  Invite friends
                </button>
              )}
              <button
                type="button"
                role="menuitem"
                className="w-full text-left px-4 native-menu-item text-white/90"
                onClick={() => { setMenuOpen(false); navigate('/legal'); }}
              >
                Legal
              </button>
              {user ? (
                <button
                  type="button"
                  data-testid="native-logout-btn"
                  className="w-full text-left px-4 native-menu-item text-rose-300"
                  onClick={logout}
                >
                  Log out
                </button>
              ) : (
                <button
                  type="button"
                  role="menuitem"
                  className="w-full text-left px-4 native-menu-item text-white/90"
                  onClick={() => { setMenuOpen(false); navigate('/login'); }}
                >
                  Sign in
                </button>
              )}
            </div>
          </>
        )}
      </div>
    </header>
  );
};

export default NativeTopBar;
